import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Button,
} from '@mui/material';

import { DataApi, IncidentCodeApi } from '@/api';
import { Page, PageHeader } from '@/components';
import { IIncidentCode, IRequiredUnitDetail } from '@/models/incident.code';
import { IUnitType } from '@/models/unit';
import { ConfirmTypes, useAgencyStore, useToastStore } from '@/store';

const ReadOnlyRequiredUnitsPanel = ({
  units,
}: {
  units: IRequiredUnitDetail[];
}) => {
  if (!units || units.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ mt: 1, fontStyle: 'italic', textAlign: 'center' }}
      >
        No required units for this incident code.
      </Typography>
    );
  }
  return (
    <TableContainer>
      <Table size="small" aria-label="required units">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Unit Type</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              Number of Units
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {units.map((unit: IRequiredUnitDetail, index: number) => (
            <TableRow key={unit.unitType?._id || `unit-${index}`}>
              <TableCell component="th" scope="row">
                {unit.unitType?.name || 'N/A'}
              </TableCell>
              <TableCell align="right">{unit.number}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const ViewIncidentCodePage = () => {
  const { incidentCodeId } = useParams<{
    incidentCodeId: string;
  }>();
  const { updateToast } = useToastStore();
  const { agencies, fetchAgencies } = useAgencyStore();
  const navigate = useNavigate();

  const [incidentCode, setIncidentCode] = useState<IIncidentCode | null>(null);
  const [unitTypes, setUnitTypes] = useState<IUnitType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAgencies = async () => {
      if (agencies.length) return;

      try {
        await fetchAgencies();
      } catch (err: any) {
        updateToast({
          type: ConfirmTypes.ERROR,
          message: err.message || 'Failed to load agencies.',
          open: true,
        });
      }
    };

    loadAgencies();
  }, [agencies.length, fetchAgencies, updateToast]);

  useEffect(() => {
    const fetchUnitTypes = async () => {
      try {
        const response = await DataApi.getUnitTypes(true);
        setUnitTypes(response.data || []);
      } catch (err: any) {
        updateToast({
          type: ConfirmTypes.ERROR,
          message: err.message || 'Failed to load unit types.',
          open: true,
        });
      }
    };
    fetchUnitTypes();
  }, []);

  useEffect(() => {
    if (!incidentCodeId) {
      setError('Incident Code ID is missing.');
      setLoading(false);
      return;
    }
    const fetchIncidentCodeDetails = async () => {
      try {
        setLoading(true);
        const data = await IncidentCodeApi.getById(incidentCodeId);

        // Map the string unitType IDs to their corresponding unit type objects
        if (
          data.requiredUnits &&
          data.requiredUnits.length > 0 &&
          unitTypes.length > 0
        ) {
          data.requiredUnits = data.requiredUnits.map((unit: any) => {
            if (typeof unit.unitType === 'string') {
              const unitTypeObj = unitTypes.find(
                (ut) => ut._id === unit.unitType,
              );
              return {
                ...unit,
                unitType: unitTypeObj || {
                  _id: unit.unitType,
                  name: 'Unknown Unit Type',
                },
              };
            }
            return unit;
          });
        }

        setIncidentCode(data);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load incident code details.');
        updateToast({
          type: ConfirmTypes.ERROR,
          message: err.message || 'Failed to load incident code details.',
          open: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchIncidentCodeDetails();
  }, [incidentCodeId, updateToast, unitTypes]);

  const backToListRoute = `/admin/account-configuration/incident-codes${window.location.search}`;

  const handleEditClick = () => {
    navigate(
      `/admin/account-configuration/incident-codes/edit/${incidentCodeId}${window.location.search}`,
    );
  };

  if (loading) {
    return (
      <Page title="Loading...">
        <PageHeader
          title="Loading Incident Code..."
          backRoute={backToListRoute}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 3,
          }}
        >
          <CircularProgress />
        </Box>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Error">
        <PageHeader
          title="Error Loading Incident Code"
          backRoute={backToListRoute}
        />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6">
            Error
          </Typography>
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        </Box>
      </Page>
    );
  }

  if (!incidentCode) {
    return (
      <Page title="Not Found">
        <PageHeader
          title="Incident Code Not Found"
          backRoute={backToListRoute}
        />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">Incident Code Not Found</Typography>
        </Box>
      </Page>
    );
  }

  const pageTitle = 'View Incident Code';
  const carStopAgencyNames =
    incidentCode.carStopAgencies
      ?.map(
        (agencyId) =>
          agencies.find((agency) => agency._id === agencyId)?.name || agencyId,
      )
      .join(', ') || 'None';
  const isUsedForCarStop = (incidentCode.carStopAgencies?.length || 0) > 0;

  return (
    <Page
      title={pageTitle}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <PageHeader
        title={pageTitle}
        backRoute={backToListRoute}
        action={
          <Button variant="contained" color="error" onClick={handleEditClick}>
            Edit
          </Button>
        }
      />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <Card variant="outlined">
            <CardHeader
              title="Incident code details"
              sx={{ backgroundColor: 'action.hover' }}
            />
            <CardContent>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Incident code
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1">{incidentCode.code}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {incidentCode.description || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={2} paddingTop={1}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Auto ready for dispatch
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap', fontWeight: 'bold' }}
                  >
                    {incidentCode.emergency !== false ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={2} paddingTop={1}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Used for Car Stop
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap', fontWeight: 'bold' }}
                  >
                    {isUsedForCarStop ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={2} paddingTop={1}>
                <Grid item xs={12} sm={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Car Stop Agencies
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap', fontWeight: 'bold' }}
                  >
                    {carStopAgencyNames}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader
              title="Required units"
              sx={{ backgroundColor: 'action.hover' }}
            />
            <CardContent>
              <ReadOnlyRequiredUnitsPanel units={incidentCode.requiredUnits} />
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </Page>
  );
};

export default ViewIncidentCodePage;
